/**
 * PASSO 1 — ORIGEM. De quem é o gado e onde ele está.
 *
 * 3D (test drive de 06/10/2026): saíram o destino e a GTA. «Não se emite GTA
 * antes de pesar o gado» e «marcar o destino antes de apartar não faz
 * sentido»: abre-se a ficha, preenche a origem e segue para a negociação. O
 * destino agora são as porteiras (Negociação) e a GTA, uma por porteira
 * (Romaneio). Ficha antiga com destino e GTA na abertura continua mostrando
 * os dois no romaneio.
 *
 * Ficha ABERTA grava sozinha a cada mudança (ver `usarGravacaoAutomatica`); o
 * botão só leva adiante («Próximo ›»). O produtor entra no cadastro ao sair
 * da tela, uma vez — não a cada pausa da digitação.
 * Ficha NOVA nasce no «Próximo ›», e não na primeira letra do nome: assim um
 * nome digitado pela metade e abandonado não gasta número de ficha nem deixa
 * produtor «Jo» no cadastro.
 */
import { useRef, useState } from 'react'
import { acharProdutorPeloNome, guardarProdutor, lerDono, lerEstado, mudar, mudarFicha, produtorVazio, proximoNumero, usarEstado } from '../dados/armazem'
import { excluir } from '../dados/acoes'
import { agora, hoje, nomeParecido, novoId, num, quando } from '../dominio/numeros'
import { lugar } from '../dominio/exportar'
import { usarFerramenta, usarPode } from '../dados/sessao'
import type { Ficha, Finalidade, Gps, Produtor } from '../dominio/tipos'
import { avisar, AvisoDeNome, Campo, confirmar, Dica, Escolha, Segmento, SinalDeSalvo, Tarja, usarGravacaoAutomatica, usarGuardaDeSaida, type Ir } from '../ui'
import { pedirReabertura } from './Romaneio'

/** O produtor como ele fica na ficha (a cópia do dia), sem as diferenças de digitação. */
function limpo(p: Produtor): Produtor {
  return { ...p, nome: p.nome.trim(), uf: p.uf.trim().toUpperCase() }
}

export function Origem({ ficha, ir }: { ficha?: Ficha; ir: Ir }) {
  const estado = usarEstado()
  // 3E: quem não pode mudar a origem (a Consulta) vê os campos só para leitura
  const pode = usarPode()
  const podeEditar = pode('editarOrigem', { encerrada: ficha?.situacao === 'encerrada' })
  const travada = !!ficha && (ficha.situacao !== 'aberta' || !podeEditar)
  const [p, setP] = useState<Produtor>(ficha ? { ...ficha.produtor } : produtorVazio())
  const [data, setData] = useState(ficha?.data ?? hoje())
  const [finalidade, setFinalidade] = useState<Finalidade>(ficha?.finalidade ?? 'Compra')
  const [obs, setObs] = useState(ficha?.obs ?? '')
  // R2D · «Venda do meu estoque» (marca na ficha; só vale com finalidade Venda). Só o gestor vê a escolha.
  const comEstoque = usarFerramenta('catira')
  const veEstoque = pode('catira') && comEstoque
  const [doEstoque, setDoEstoque] = useState(!!ficha?.vendaDoEstoque)
  /** ficha que já existe grava a marca na hora; ficha nova leva a marca ao nascer, no «Próximo ›» */
  function mudarDoEstoque(v: boolean) {
    setDoEstoque(v)
    if (ficha && ficha.situacao === 'aberta' && !!ficha.vendaDoEstoque !== v) {
      mudarFicha(ficha.id, (x) => { if (v) x.vendaDoEstoque = true; else delete x.vendaDoEstoque })
    }
  }
  const [buscandoGps, setBuscandoGps] = useState(false)
  const [erroGps, setErroGps] = useState('')

  const campo = (k: keyof Produtor) => (v: string) => setP((x) => ({ ...x, [k]: v }))

  /**
   * R1C · «Nova pesagem» preenchida sumia sem aviso ao tocar em Fichas (a
   * ficha só nasce no «Próximo»). Com algo digitado, o app pergunta antes de
   * descartar. Ficha que já existe grava sozinha: nada a perguntar.
   */
  const nascendo = useRef(false)
  usarGuardaDeSaida(() => {
    if (ficha || nascendo.current) return null
    const preenchido = [p.nome, p.doc, p.ie, p.propriedade, p.endereco, p.municipio, p.telefone, obs].some((x) => String(x ?? '').trim()) || !!p.gps
    return preenchido ? 'O que foi preenchido nesta nova pesagem ainda não virou ficha (ela nasce no «Próximo ›») e vai sumir.' : null
  })
  const doCadastro = acharProdutorPeloNome(estado, p.nome)
  const parecido = doCadastro ? undefined : nomeParecido(estado.produtores.filter((x) => x.ativo !== false), p.nome)

  // ficha aberta: grava sozinha. A chave é o que vai para a ficha — sem nome, não grava.
  const aberta = !!ficha && ficha.situacao === 'aberta' && podeEditar
  const rascunho = { produtor: limpo(p), data, finalidade, obs: obs.trim() }
  const { situacao, sair } = usarGravacaoAutomatica({
    ativo: aberta && !!p.nome.trim(),
    chave: JSON.stringify(rascunho),
    gravar: () => {
      if (!ficha) return
      const f = lerEstado().fichas.find((x) => x.id === ficha.id)
      if (!f || f.situacao !== 'aberta') return
      // nada mudou de fato: não mexe (não vira pendência à toa)
      if (JSON.stringify({ produtor: f.produtor, data: f.data, finalidade: f.finalidade, obs: f.obs }) === JSON.stringify(rascunho)) return
      mudarFicha(ficha.id, (x) => { Object.assign(x, rascunho) })
    },
    // ao sair: o produtor entra (ou se atualiza) no cadastro uma vez, e a ficha guarda a versão do cadastro
    aoSair: () => {
      if (!ficha || !p.nome.trim()) return
      let guardado: Produtor | null = null
      mudar((e) => {
        const f = e.fichas.find((x) => x.id === ficha.id)
        if (!f || f.situacao !== 'aberta') return
        const d = limpo(p)
        const noCadastro = (d.id && e.produtores.find((x) => x.id === d.id)) || acharProdutorPeloNome(e, d.nome)
        const igual = (a: Produtor, b: Produtor) => JSON.stringify({ ...a, alteradoEm: '' }) === JSON.stringify({ ...b, alteradoEm: '' })
        // o cadastro só muda (e só vira pendência) quando o produtor mudou de verdade
        guardado = noCadastro && igual(noCadastro, { ...d, id: noCadastro.id }) ? { ...noCadastro } : guardarProdutor(e, d)
        if (JSON.stringify(f.produtor) !== JSON.stringify(guardado)) { f.produtor = guardado; f.alteradaEm = agora() }
      })
      if (guardado) setP(guardado)
    },
  })


  function mudarNome(v: string) {
    const achado = acharProdutorPeloNome(estado, v)
    // nome do cadastro: preenche o resto, inclusive a localização já marcada
    if (achado && achado.id !== p.id) setP({ ...achado, nome: v })
    else setP((x) => ({ ...x, nome: v, id: achado ? x.id : '' }))
  }

  function marcarGps() {
    if (!('geolocation' in navigator)) { setErroGps('Este aparelho não informa localização.'); return }
    setBuscandoGps(true); setErroGps('')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const gps: Gps = { lat: pos.coords.latitude, lon: pos.coords.longitude, precisao: Math.round(pos.coords.accuracy), em: agora() }
        setP((x) => ({ ...x, gps }))
        setBuscandoGps(false)
        avisar('Localização marcada')
      },
      (err) => {
        setBuscandoGps(false)
        setErroGps(err.code === err.PERMISSION_DENIED
          ? 'A localização foi bloqueada. Libere em: cadeado ao lado do endereço › Permissões › Localização.'
          : err.code === err.TIMEOUT
            ? 'O GPS demorou demais. Vá para um lugar aberto, longe do telhado, e tente de novo.'
            : 'Não foi possível ler o GPS agora. Tente de novo.')
      },
      { enableHighAccuracy: true, timeout: 30_000, maximumAge: 0 },
    )
  }

  /** «Próximo ›»: ficha aberta já está gravada (só termina o que faltou); ficha nova nasce aqui. */
  function proximo() {
    if (ficha) {
      if (aberta && !p.nome.trim()) { window.alert('Informe o nome do produtor.'); return }
      sair()
      ir({ tela: 'ficha', id: ficha.id, aba: 'negociacao' })
      return
    }
    if (!p.nome.trim()) { window.alert('Informe o nome do produtor.'); return }
    const id = novoId()
    mudar((e) => {
      const a = e.ajustes
      const f: Ficha = {
        id, numero: proximoNumero(e, data), data, criadaEm: agora(), alteradaEm: agora(),
        // destino e GTA vazios (e não ausentes): o app antigo de outro celular lê os dois
        situacao: 'aberta', finalidade, produtor: guardarProdutor(e, p), destino: { nome: '', municipio: '', uf: '' }, gta: '', obs: obs.trim(),
        // os % da ficha ficam como padrão do lote sem os seus; cada lote novo já nasce com os dele
        forma: 'curral', lotes: [], financeiroPct: a.financeiroPct, funruralPct: a.funruralPct,
        registros: [], pagamentos: [], custos: [], historico: [{ em: agora(), oque: 'Aberta' }], versao: 1, emitente: null,
        pesadoPor: lerDono()?.nome ?? '',
        // R2B: o gestor pesou ele mesmo — a ficha não oferece comissão de comprador
        ...(lerDono()?.papel === 'gestor' ? { pesadoPorGestor: true } : {}),
        // R1C: ficha nova arredonda a arroba de cada lote em 2 casas, como no papel (contas.ts)
        arrobaEmDuasCasas: true,
        // R2D: a venda do meu estoque sai sozinha ao encerrar
        ...(finalidade === 'Venda' && doEstoque && veEstoque ? { vendaDoEstoque: true } : {}),
      }
      e.fichas.push(f)
    })
    nascendo.current = true // virou ficha: nada a descartar
    ir({ tela: 'ficha', id, aba: 'negociacao' })
  }

  return (
    <div>
      {travada && ficha && ficha.situacao !== 'aberta' && pode('reabrir') && (
        <Tarja>
          Ficha {ficha.situacao}. Para corrigir a origem, reabra a ficha.{' '}
          {ficha.situacao === 'encerrada' && <button className="mini" onClick={() => { void pedirReabertura(ficha) }}>Reabrir</button>}
        </Tarja>
      )}
      <fieldset disabled={travada} className="sem-borda">
        <h2 className="secao">Negócio</h2>
        <div className="cartao">
          <div className="linha">
            <Campo rotulo="Data" tipo="date" valor={data} mudar={setData} />
            <Escolha rotulo="Finalidade" valor={finalidade} mudar={setFinalidade}
              opcoes={[['Compra', 'Compra'], ['Venda', 'Venda'], ['Conferência', 'Conferência']]} />
          </div>
          {/* R2D · «Venda do meu estoque»: só o gestor, com o Meu estoque ligado */}
          {finalidade === 'Venda' && veEstoque && (
            <div style={{ marginTop: 8 }}>
              <Segmento valor={doEstoque ? 'estoque' : 'comum'} mudar={(v) => mudarDoEstoque(v === 'estoque')}
                opcoes={[['comum', 'Venda comum'], ['estoque', 'Venda do meu estoque']]} />
              <div className="mini-dica">{doEstoque
                ? 'O gado sai do Meu estoque sozinho ao encerrar, categoria por categoria, pelo custo médio de cada uma. Os lotes usam as categorias do estoque.'
                : 'Venda do meu estoque: escolha quando vender o gado que está no seu estoque — ele sai sozinho ao encerrar.'}</div>
            </div>
          )}
        </div>

        {/* R2B: na Venda, quem está no cadastro é o COMPRADOR (antes a tela dizia «Produtor») */}
        <h2 className="secao">{finalidade === 'Venda' ? 'Comprador — quem compra o seu gado' : 'Origem — produtor e propriedade'}</h2>
        <div className="cartao">
          <Campo rotulo={finalidade === 'Venda' ? 'Comprador' : finalidade === 'Conferência' ? 'Dono do gado' : 'Produtor (vendedor)'} valor={p.nome} mudar={mudarNome} lista="lista-produtores"
            dica={finalidade === 'Venda' ? 'nome de quem compra' : 'nome de quem é o gado'} />
          <datalist id="lista-produtores">{estado.produtores.map((x) => <option key={x.id} value={x.nome} />)}</datalist>
          <AvisoDeNome nome={p.nome} doCadastro={!!doCadastro} parecido={parecido?.nome} usar={mudarNome}
            novo={'Produtor novo: entra no cadastro ' + (ficha ? 'ao sair desta tela.' : 'no «Próximo».')} />
          <div className="linha">
            <Campo rotulo="CPF/CNPJ" valor={p.doc} mudar={campo('doc')} modo="numeric" />
            <Campo rotulo="Inscrição estadual" valor={p.ie} mudar={campo('ie')} />
          </div>
          <Campo rotulo="Propriedade (fazenda, sítio)" valor={p.propriedade} mudar={campo('propriedade')} />
          <Campo rotulo="Endereço / como chegar" valor={p.endereco} mudar={campo('endereco')} />
          <div className="linha">
            <div style={{ flex: 3 }}><Campo rotulo="Município" valor={p.municipio} mudar={campo('municipio')} /></div>
            <Campo rotulo="UF" valor={p.uf} mudar={campo('uf')} max={2} maiusculas />
          </div>
          <Campo rotulo="Telefone" valor={p.telefone} mudar={campo('telefone')} modo="tel" />

          <div className="gps">
            {travada ? (
              p.gps ? <div>📍 <b>{num(p.gps.lat, 5)}, {num(p.gps.lon, 5)}</b> · precisão de {p.gps.precisao} m ·{' '}
                <a href={`https://www.google.com/maps?q=${p.gps.lat},${p.gps.lon}`} target="_blank" rel="noreferrer">ver no mapa</a></div>
                : <div className="mini-dica">Sem localização marcada.</div>
            ) : p.gps ? (
              <>
                <div>📍 <b>{num(p.gps.lat, 5)}, {num(p.gps.lon, 5)}</b> · precisão de {p.gps.precisao} m</div>
                <div className="mini-dica">Marcada em {quando(p.gps.em)} ·{' '}
                  <a href={`https://www.google.com/maps?q=${p.gps.lat},${p.gps.lon}`} target="_blank" rel="noreferrer">ver no mapa</a></div>
                <button type="button" className="botao fantasma pequeno" onClick={marcarGps} disabled={buscandoGps}>
                  {buscandoGps ? 'Lendo o GPS…' : 'Marcar de novo'}</button>
              </>
            ) : (
              <>
                <button type="button" className="botao fantasma" onClick={marcarGps} disabled={buscandoGps}>
                  {buscandoGps ? 'Lendo o GPS…' : '📍 Habilitar localização da propriedade'}</button>
                <div className="mini-dica">Funciona sem sinal de celular. Marque estando na propriedade; da próxima vez ela já vem do cadastro.</div>
              </>
            )}
            {erroGps && <div className="alerta">{erroGps}</div>}
          </div>
        </div>

        <h2 className="secao">Observações</h2>
        <div className="cartao">
          <label className="campo"><span>Observações</span><textarea rows={2} value={obs} onChange={(e) => setObs(e.target.value)} /></label>
          <div className="mini-dica">As porteiras (para onde o gado vai) se montam na negociação; o nº da GTA, depois da pesagem, no romaneio.</div>
          {/* ficha de antes da 3D: o que foi anotado na abertura continua valendo e saindo no romaneio */}
          {ficha && (ficha.destino?.nome || ficha.gta) && (
            <div className="mini-dica">Anotado na abertura desta ficha: {[ficha.destino?.nome && `destino ${lugar(ficha.destino)}`, ficha.gta && `GTA ${ficha.gta}`].filter(Boolean).join(' · ')} — continua no romaneio.</div>
          )}
        </div>
      </fieldset>
      {/* fora do fieldset: em ficha encerrada a origem trava, mas o «Próximo» só leva adiante */}
      <div className="rodape-passo">
        <button className="botao grande" onClick={proximo}>Próximo ›</button>
        {aberta && <SinalDeSalvo situacao={situacao} />}
      </div>
      {ficha && ficha.situacao === 'aberta' && ficha.registros.length === 0 && pode('excluirFicha') && (
        <>
          <Dica>Ficha aberta por engano e ainda sem nenhuma pesagem pode ser excluída.</Dica>
          <button className="botao perigo" onClick={async () => {
            if (await confirmar({ titulo: `Excluir a ficha nº ${ficha.numero}?`, texto: 'Ela ainda não tem nenhuma pesagem.', sim: 'Excluir', perigo: true }) && excluir(ficha.id)) ir({ tela: 'fichas' })
          }}>Excluir ficha</button>
        </>
      )}
    </div>
  )
}
